import { html } from "lit-html"
import { virtual, useState } from "haunted"
import DateTime from "./date-time"
import DocumentLinkList from "./doc-links"
import TagList from "./tag-list"
import CalLinks from "./cal-links"
import SocialLinks from "./social-links"
import Location from "./location"
import ActionMenu from "../generic/action-menu"
import { keyClickWrapper } from "../utils"
import markdown from "../utils/markdown"
import {
  CREATE_ASSIGNMENT_PATH,
  CREATE_ASSIGNMENT_MEETING_PARAM,
  EDIT_PATH,
  CLONE_PATH,
} from "../constants"

const MeetingLocation = ({ location }) => {
  let displayObj = null
  if (location && location.display !== "Chicago, IL") {
    displayObj = { display: location.display }
  }
  return displayObj
}

const MeetingRow = virtual(
  ({
    id,
    name,
    url,
    start_date,
    start_time,
    shortened_timezone,
    recurring_pattern,
    recurring_patterns,
    tags,
    location,
    gcal_link,
    agency,
    documents,
    can_edit,
    status,
  }) => {
    const [expanded, setExpanded] = useState(false)
    const canceled = status.toLowerCase().includes("cancel")

    return html`
      <div
        class="meeting filter-result-row group"
        role="button"
        tabindex="0"
        @click=${(e) => {
          if (e && e.target.tagName.toLowerCase() === "a") {
            return
          }
          setExpanded(!expanded)
        }}
        @keydown=${(e) => keyClickWrapper(e, () => setExpanded(!expanded))}
        aria-controls=${`item-${id}`}
        aria-expanded=${expanded.toString()}
      >
        <div class="w-3/4 lg:w-1/3 order-1 p-3">
          <p>
            <span
              class="text-blue group-hover:text-black group-focus:text-gray-dark group-focus:font-bold"
              >${name}</span
            >
          </p>
          <p>${agency ? agency.name : ""}</p>
        </div>
        <div class="w-7/12 lg:w-1/4 order-3 lg:order-2 p-3">
          ${DateTime({
            start_date,
            start_time,
            shortened_timezone,
            canceled,
          })}
          ${recurring_patterns.map(
            (pattern) =>
              html`<p
                class="italic ${pattern.id === recurring_pattern
                  ? "font-bold"
                  : ""}"
              >
                * ${pattern.name}
              </p>`
          )}
        </div>
        <div
          class="w-5/12 lg:w-1/6 order-4 lg:order-3 text-right lg:text-left p-3"
        >
          ${TagList(tags)}
        </div>
        <div
          class="w-1/4 order-2 lg:order-4 flex flex-row items-center justify-end text-right p-3"
        >
          ${canceled
            ? html`
                <div
                  class="status-block assignment-status canceled hidden lg:inline-flex mr-2"
                >
                  Canceled
                </div>
              `
            : ``}
          ${ActionMenu({
            children: html`
              ${can_edit
                ? html`
                    <a
                      class="dropdown-item"
                      role="menuitem"
                      href="${CREATE_ASSIGNMENT_PATH}?${CREATE_ASSIGNMENT_MEETING_PARAM}=${id}"
                    >
                      Convert to assignment
                    </a>
                    <a
                      class="dropdown-item"
                      role="menuitem"
                      href="${url}${EDIT_PATH}"
                    >
                      Edit meeting
                    </a>
                    <a
                      class="dropdown-item"
                      role="menuitem"
                      href="${url}${CLONE_PATH}"
                    >
                      Duplicate meeting
                    </a>
                  `
                : ""}

              <a class="dropdown-item" role="menuitem" href=${url}>
                View meeting details
              </a>
              ${agency && agency.url
                ? html`
                    <a class="dropdown-item" role="menuitem" href=${agency.url}>
                      View agency details
                    </a>
                  `
                : ""}
              ${CalLinks({ url, gcal_link })}
              <hr class="dropdown-divider" />
              ${SocialLinks({ url })}
            `,
          })}
        </div>
        ${canceled
          ? html`
              <div class="w-full lg:hidden order-5 p-3">
                <div class="status-block assignment-status canceled">
                  Canceled
                </div>
              </div>
            `
          : ``}
        <hr class="filter-result-row-divider" />
      </div>
      <div
        class="collapse-row py-1"
        id=${`item-${id}`}
        aria-hidden=${(!expanded).toString()}
      >
        ${expanded
          ? html`
              <div class="content p-5 max-w-570">
                <p>${Location(MeetingLocation({ location }))}</p>
                ${!agency
                  ? html` <p>This meeting is not hosted by a public agency</p> `
                  : ""}
                ${agency && agency.description
                  ? markdown(agency.description)
                  : html`
                      <p>A description for this agency is not available</p>
                    `}
                ${DocumentLinkList(documents, url)}
                <a
                  href=${url}
                  data-link="details"
                  class="flex items-center justify-center mt-6 h-10 w-full bg-blue text-white rounded hover:bg-blue-dark focus:bg-blue-dark"
                >
                  View full meeting details
                </a>
              </div>
            `
          : ""}
      </div>
    `
  }
)

export default MeetingRow
